import { createFileRoute } from '@tanstack/react-router'
import { Plus, DollarSign } from 'lucide-react'
import { useState } from 'react'

import { SalaryForm } from '@/components/hr/salary/salary-form'
import { useCurrencyFormatter } from '@/hooks'
import { useSalaries, type SalaryListParams, type SalaryWithEmployee } from '@/hooks'

export const Route = createFileRoute('/_authenticated/hr/salaries/')({
  component: SalaryListPage,
})



function SalaryListPage() {
  const [filters, setFilters] = useState<SalaryListParams>({ page: 1, pageSize: 20 })
  const [showCreateForm, setShowCreateForm] = useState(false)

  const { data, isLoading, error } = useSalaries(filters)
  const { formatMoney } = useCurrencyFormatter()

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }))
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-destructive">加载失败: {error.message}</div>
      </div>
    )
  }

  const salaries = data?.data ?? []
  const meta = data?.meta

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">薪资设置</h1>
          <p className="text-muted-foreground">管理员工薪资信息</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          设置薪资
        </button>
      </div>

      {showCreateForm && (
        <SalaryForm onClose={() => setShowCreateForm(false)} onSuccess={() => setShowCreateForm(false)} />
      )}

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="text-muted-foreground">加载中...</div>
        </div>
      ) : salaries.length === 0 ? (
        <div className="flex h-64 flex-col items-center justify-center gap-4 rounded-lg border border-dashed">
          <DollarSign className="h-12 w-12 text-muted-foreground" />
          <div className="text-center">
            <p className="text-lg font-medium">暂无薪资记录</p>
            <p className="text-sm text-muted-foreground">点击上方按钮设置员工薪资</p>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left text-sm font-medium">员工</th>
                <th className="px-4 py-3 text-left text-sm font-medium">基本工资</th>
                <th className="px-4 py-3 text-left text-sm font-medium">津贴</th>
                <th className="px-4 py-3 text-left text-sm font-medium">生效日期</th>
                <th className="px-4 py-3 text-left text-sm font-medium">备注</th>
              </tr>
            </thead>
            <tbody>
              {salaries.map((salary: SalaryWithEmployee) => (
                <tr key={salary.id} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium">{salary.employee?.name || '-'}</p>
                      <p className="text-sm text-muted-foreground">{salary.employee?.employeeNo}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-medium">{formatMoney(salary.baseSalary)}</td>
                  <td className="px-4 py-3 text-sm">{salary.allowance ? formatMoney(salary.allowance) : '-'}</td>
                  <td className="px-4 py-3 text-sm">{salary.effectiveDate}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{salary.remark || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {meta && meta.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            共 {meta.total} 条记录，第 {meta.page}/{meta.totalPages} 页
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(meta.page - 1)}
              disabled={meta.page <= 1}
              className="rounded-md border px-3 py-1.5 text-sm disabled:opacity-50"
            >
              上一页
            </button>
            <button
              onClick={() => handlePageChange(meta.page + 1)}
              disabled={meta.page >= meta.totalPages}
              className="rounded-md border px-3 py-1.5 text-sm disabled:opacity-50"
            >
              下一页
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
